import classNames from 'classnames';
import { isNumber } from 'lodash';
import React, { useContext, useEffect, useState } from 'react';
import { type IFontStroke, type ITextLayerProps } from '@meitu/saas-editor-core/es/version-types';
import IconFont from '@meitu/xdesign-icon-font';
import ColorInputGroup from '@/components/color-input-group';
import { SimpleEffectPropsContainer } from '@/components/simple-effect-props-container';
import { useEditor } from '@/hooks/use-editor';
import { useGlobalWidget } from '@/hooks/use-global-widget';
import { useLocaleStore } from '@/i18n';
import { TextPanelContext } from '@/panels/text-layer/context';
import { useLayerProps } from '@/panels/text-layer/hooks';
import { Tracker } from '@/panels/text-layer/utils';
import './style.less';
import { TextLayerExtension } from '@meitu/saas-editor-core/es/editor/layer-extensions/text-layer';

type Props = {};

export const StrokeInspector: React.FC<Props> = () => {
    const { layerId, recordId, onOpenSubPanel, isIntl } = useContext(TextPanelContext)!;

    const { $t } = useLocaleStore();
    const editor = useEditor();

    const props = useLayerProps(editor, (layer) => {
        if (!layer) return null;

        const layerData = layer.data as ITextLayerProps;
        const { textStroke, shapeExtension } = layerData;

        return {
            textStroke: textStroke,
            shapeExtension,
        };
    });

    const popoverWidgetRef = useGlobalWidget(editor, false);

    const strokes = props ? props.textStroke || [] : ([] as IFontStroke[]);
    const shapeExtension = props ? props.shapeExtension : null;
    const hasVisibleStroke = strokes.filter((stroke) => !stroke.hidden).length > 0;
    const strokeLength = strokes.length;

    const max = shapeExtension ? 1 : 3;

    const [activeStrokeIndex, setActiveStrokeIndex] = useState(strokeLength - 1);

    useEffect(() => {
        setActiveStrokeIndex(strokeLength - 1);
    }, [strokeLength]);

    const add = () => {
        if (strokes.length >= max) return;

        editor.getInstance(TextLayerExtension).addStroke(layerId);

        Tracker.addStroke({
            recordId,
            stageType: editor.stage.getType(),
            panelPosition: 'shortcut',
        });
    };

    const setVisibility = (flag: boolean, index?: number) => {
        editor.getInstance(TextLayerExtension).setEffectVisibility(layerId, 'textStroke', flag, index);
        Tracker.toggleStroke({
            recordId,
            stageType: editor.stage.getType(),
            panelPosition: 'shortcut',
        });
    };

    const remove = (index?: number) => {
        editor.getInstance(TextLayerExtension).removeEffect(layerId, 'textStroke', index);
    };

    const handleColorPickerOpenChange = (open: boolean) => {
        popoverWidgetRef.current = open;

        if (open) {
            Tracker.openColorPicker({
                recordId,
                stageType: editor.stage.getType(),
                kind: 'textStroke',
                panelPosition: 'shortcut',
            });
        }
    };

    const adjustColor = (index: number, value: string, options = { writeHistory: true }) => {
        const payload = { idx: index, data: { color: value } };
        editor.getInstance(TextLayerExtension).adjustStrokeProps(layerId, payload, options);

        if (options.writeHistory) {
            Tracker.adjustStrokeProps({
                recordId,
                stageType: editor.stage.getType(),
                panelPosition: 'shortcut',

                prop: 'color',
                value,
            });
        }
    };

    const adjustWidth = (index: number, value: number | null, options = { writeHistory: true }) => {
        if (!isNumber(value)) return;

        const width = Math.round(value);
        const payload = { idx: index, data: { width } };
        editor.getInstance(TextLayerExtension).adjustStrokeProps(layerId, payload, options);

        if (options.writeHistory) {
            Tracker.adjustStrokeProps({
                recordId,
                stageType: editor.stage.getType(),
                panelPosition: 'shortcut',

                prop: 'width',
                value,
            });
        }
    };
    const strokeGroup = ({ stroke, index }: { stroke: IFontStroke; index: number }) => {
        const hidden = Boolean(stroke.hidden);
        const color = stroke.color;
        const width = stroke.width;

        return (
            <ColorInputGroup
                key={`stroke-group-${index}`}
                disabled={hidden}
                width={width}
                color={color}
                showInput={!isIntl}
                onChange={(val) => adjustWidth(index, val, { writeHistory: false })}
                onChangeAfter={(val) => adjustWidth(index, val, { writeHistory: true })}
                onAdjustColor={(color) => adjustColor(index, color, { writeHistory: false })}
                onAdjustColorAfter={(color, isBrandColor) => {
                    adjustColor(index, color, { writeHistory: true });
                    if (isBrandColor) {
                        window._stat_track?.('hb_layers_func_use', {
                            func_id: '1_107',
                        });
                    }
                }}
                onColorPickerOpenChange={handleColorPickerOpenChange}
                onToggleBizTab={(bizTab) => {
                    if (bizTab === 'team') {
                        window._stat_track?.('hb_layers_func_click', {
                            func_id: '1_107',
                        });
                    }
                }}
            />
        );
    };

    const strokeDetail = () => {
        if (strokeLength === 1) return null;

        const activeStroke = strokes[activeStrokeIndex];
        if (!activeStroke) return null;

        const hidden = Boolean(activeStroke.hidden);
        const groupName = `${$t((v) => v.textStyleAdd.stroke)}${activeStrokeIndex + 1}`;

        return (
            <div
                className={classNames('text-stroke-ctrl__detail', {
                    'text-stroke-ctrl__detail--disabled': hidden,
                })}
                key={`stroke-detail-${activeStrokeIndex}`}
            >
                <div className="text-stroke-ctrl__detail-name">{groupName}</div>
                <div className="text-stroke-ctrl__detail-content">
                    {strokeGroup({ stroke: activeStroke, index: activeStrokeIndex })}
                    <div className="text-stroke-ctrl__detail-icons">
                        <div
                            className="text-stroke-ctrl__detail-icon"
                            onClick={() => onOpenSubPanel('textEffect', 'stroke')}
                        >
                            <IconFont name="DoubleAdjustBold" fontSize={14} />
                        </div>

                        <div
                            className="text-stroke-ctrl__detail-icon"
                            onClick={() => setVisibility(!hidden, activeStrokeIndex)}
                        >
                            <IconFont name={hidden ? 'InvisibleBold' : 'VisibleBold'} fontSize={14} />
                        </div>
                        <div className="text-stroke-ctrl__detail-icon" onClick={() => remove(activeStrokeIndex)}>
                            <IconFont name="TrashCanBold" fontSize={14} />
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    return (
        <SimpleEffectPropsContainer
            title={$t((v) => v.textStyleAdd.stroke)}
            showAdd={strokes.length < max}
            onAdd={add}
            onRemove={() => remove()}
            showIcons={strokeLength > 0}
            showEntrance={strokeLength === 1}
            onToggleVisible={() => setVisibility(hasVisibleStroke ? true : false)}
            visible={hasVisibleStroke}
            detailChildren={strokeDetail()}
            onJump2SubPanel={() => onOpenSubPanel('textEffect', 'stroke')}
        >
            {strokeLength > 0 ? (
                <div className="text-stroke-ctrl">
                    {strokes.map((stroke, index) => {
                        return strokeLength === 1 ? (
                            <React.Fragment key={`stroke-color-${index}`}>
                                {strokeGroup({ stroke, index })}
                            </React.Fragment>
                        ) : (
                            <div
                                className={classNames('text-stroke-ctrl__color', {
                                    'text-stroke-ctrl__color--active': activeStrokeIndex === index,
                                    'text-stroke-ctrl__color--disabled': stroke.hidden,
                                })}
                                onClick={() => setActiveStrokeIndex(index)}
                                key={`stroke-color-${index}`}
                            >
                                <div
                                    className="text-stroke-ctrl__color-item"
                                    style={{ backgroundColor: stroke.color }}
                                ></div>
                            </div>
                        );
                    })}
                </div>
            ) : null}
        </SimpleEffectPropsContainer>
    );
};
